import { useCallback, useEffect, useRef } from 'react'
import Basemap from '@arcgis/core/Basemap'
import FeatureLayer from '@arcgis/core/layers/FeatureLayer'
import Map from '@arcgis/core/Map'
import { SimpleRenderer } from '@arcgis/core/renderers'
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol'
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol'
import MapView from '@arcgis/core/views/MapView'
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery'
import Expand from '@arcgis/core/widgets/Expand'
import LayerList from '@arcgis/core/widgets/LayerList'
import Legend from '@arcgis/core/widgets/Legend'

import { useGetBaseSettingsQuery } from 'app/services/api/basesettings'
import {
  getCenterlineUrl,
  getDefaultCenterCoordinates,
  getStructureLayerUrl,
  getWisTitle
} from 'common/utils/mapUtils'
import { getDetailsValueByKey } from 'common/utils/settingsUtils'

import { useEsriAuth } from './Auth'
import { IExtentQueryResponse, IMapComponent } from './interfaces/Maps'
import { handleDocumentClick, handleExpand } from './utils/esriWidgetHelper'
import { formatTableNode as formatTableNodeFn } from './utils/helperFuntions'
import { setupFetchInterceptor } from './utils/setupFetchInterceptor'

import './style.scss'

const DEFAULT_ZOOM = 9
const centerlineColor = [255, 255, 255, 255]
const structureColor = [81, 176, 176, 153]
const structureOutlineColor = [88, 89, 91, 255]

const structureFields = [
  {
    fieldName: 'StructureTypeCode',
    label: 'Structure Type'
  },
  {
    fieldName: 'ValuationUsd',
    label: 'Valuation USD'
  },
  {
    fieldName: 'HumanOccupancyFlag',
    label: 'Human Occupancy'
  },
  {
    fieldName: 'OccupantCount',
    label: 'Occupant Count'
  },
  {
    fieldName: 'TotalOutdoorOccupantCount',
    label: 'Total Outdoor Occupant Count'
  },
  {
    fieldName: 'TotalIndoorOccupantCount',
    label: 'Total Indoor Occupant Count'
  },
  {
    fieldName: 'MobilityFactor',
    label: 'Mobility Factor'
  },
  {
    fieldName: 'WoodenStructureFlag',
    label: 'Wooden Structure'
  },
  {
    fieldName: 'Units',
    label: 'Units'
  },
  {
    fieldName: 'Stories',
    label: 'Stories'
  },
  {
    fieldName: 'IdentifiedSiteFlag',
    label: 'Identified Site'
  },
  {
    fieldName: 'Longitude',
    label: 'Longitude'
  },
  {
    fieldName: 'Latitude',
    label: 'Latitude'
  }
]

const MapComponent = ({
  assessmentId,
  layer2Name,
  featureLayerTitle,
  featureLayerUrl,
  featureLayerFilterQuery,
  renderer,
  featureLayerUrl2,
  featureLayerTitle2,
  contentFields,
  wisServiceUrl,
  onStructureLayerVisibilityChange,
  showStructureLayer = false
}: IMapComponent) => {
  const mapDomRef = useRef() as React.MutableRefObject<HTMLDivElement>
  const dataset1LayerRef = useRef<FeatureLayer>()
  const structureLayerRef = useRef<FeatureLayer>()
  const showStructureLayerRef = useRef(showStructureLayer)
  const { currentData: baseSettings } = useGetBaseSettingsQuery()
  const { isEsriTokenSetupComplete } = useEsriAuth()

  const mapCenterCoordinates = getDetailsValueByKey(
    baseSettings,
    'MapSettings',
    'Center'
  )

  useEffect(() => {
    const cleanup = setupFetchInterceptor()

    return () => cleanup() // Restore original fetch
  }, [])

  useEffect(() => {
    showStructureLayerRef.current = showStructureLayer
  }, [showStructureLayer])

  // TODO: add check to prevent 2nd rendering
  useEffect(() => {
    if (!isEsriTokenSetupComplete) {
      return
    }

    const map = new Map({
      basemap: 'satellite'
    })

    const view = new MapView({
      map: map,
      center: getDefaultCenterCoordinates(mapCenterCoordinates),
      zoom: DEFAULT_ZOOM,
      spatialReference: {
        wkid: 3857
      },
      constraints: {
        minZoom: 3
      },
      container: mapDomRef.current,
      popup: {
        highlightEnabled: true,
        dockEnabled: true,
        dockOptions: {
          breakpoint: false,
          buttonEnabled: false
        }
      }
    })

    const originalBaselineRenderer = new SimpleRenderer({
      label: 'Route',
      symbol: new CIMSymbol({
        data: {
          type: 'CIMSymbolReference',
          symbol: {
            type: 'CIMLineSymbol',
            symbolLayers: [
              {
                type: 'CIMSolidStroke',
                enable: true,
                capStyle: 'Round',
                joinStyle: 'Round',
                width: 6,
                color: centerlineColor
              }
            ]
          }
        }
      })
    })

    const originalStructureRenderer = new SimpleRenderer({
      symbol: new SimpleFillSymbol({
        color: structureColor,
        outline: {
          color: structureOutlineColor,
          width: 1
        }
      })
    })

    const originalBaselineLayer = new FeatureLayer({
      title: 'Centerline',
      url: getCenterlineUrl(baseSettings),
      renderer: originalBaselineRenderer,
      definitionExpression: ''
    })

    const structureLayer = new FeatureLayer({
      title: 'Structure',
      url: getStructureLayerUrl(baseSettings),
      renderer: originalStructureRenderer,
      opacity: 0.6,
      popupTemplate: {
        title: 'Structure Info',
        content: [
          {
            type: 'fields',
            fieldInfos: structureFields
          }
        ],
        lastEditInfoEnabled: false
      },
      definitionExpression: '',
      visible: showStructureLayerRef.current
    })

    structureLayerRef.current = structureLayer

    map.add(originalBaselineLayer)
    map.add(structureLayer)

    if (onStructureLayerVisibilityChange) {
      structureLayer.watch('visible', (val) => {
        onStructureLayerVisibilityChange(val)
      })
    }

    // Baseline feature layer
    const baselineLayer = new FeatureLayer({
      title: featureLayerTitle,
      url: featureLayerUrl,
      renderer: renderer,
      outFields: ['*'],
      definitionExpression: featureLayerFilterQuery,
      popupTemplate: {
        title: featureLayerTitle + ' Segment Info',
        content: [
          {
            type: 'fields',
            fieldInfos: contentFields
          }
        ],
        lastEditInfoEnabled: false
      }
    })

    dataset1LayerRef.current = baselineLayer

    // Wait for the layer and view to be ready so as to ensure successful goTo navigation
    Promise.all([baselineLayer.when(), view.when()])
      .then(async () => {
        const query = baselineLayer.createQuery()
        // Ensure query spatial reference matches the view
        query.outSpatialReference = view.spatialReference

        return baselineLayer.queryExtent(query)
      })
      .then((response: IExtentQueryResponse) => {
        if (
          response.extent.center.longitude === 0 &&
          response.extent.center.latitude === 0
        ) {
          view.goTo({
            center: getDefaultCenterCoordinates(mapCenterCoordinates)
          })
        } else {
          view.goTo(response.extent)
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error(err)
        } else {
          console.log('Skipped view render', err)
        }
      })

    map.add(baselineLayer)

    // Comparing two baselines
    if (!assessmentId) {
      if (featureLayerUrl2) {
        // 2nd Baseline layer
        const baseline2Layer = new FeatureLayer({
          title: featureLayerTitle2,
          url: featureLayerUrl2,
          renderer: renderer,
          definitionExpression: featureLayerFilterQuery,
          outFields: ['*'],
          popupTemplate: {
            title: featureLayerTitle2 + ' Segment Info',
            content: [
              {
                type: 'fields',
                fieldInfos: contentFields
              }
            ]
          },
          visible: false
        })

        map.add(baseline2Layer)
      }
    } else {
      const index = featureLayerFilterQuery?.indexOf('AND ((RiskTotal')
      const wisLayer = new FeatureLayer({
        title: getWisTitle(layer2Name),
        url: wisServiceUrl,
        outFields: ['*'],
        renderer: renderer,
        definitionExpression:
          index !== undefined && index > 0
            ? featureLayerFilterQuery?.substring(0, index) +
              ` AND AssessmentId = ${assessmentId}`
            : featureLayerFilterQuery + ` AND AssessmentId = ${assessmentId}`,
        popupTemplate: {
          title: getWisTitle(layer2Name) + ' Segment Info',
          content: [
            {
              type: 'fields',
              fieldInfos: [
                {
                  fieldName: 'RouteName',
                  label: 'Route Name'
                },
                {
                  fieldName: 'FromMeasure',
                  label: 'From Measure',
                  format: {
                    places: 2,
                    digitSeparator: true
                  }
                },
                {
                  fieldName: 'ToMeasure',
                  label: 'To Measure',
                  format: {
                    digitSeparator: true,
                    places: 2
                  }
                },
                {
                  fieldName: 'FromDate',
                  label: 'From Date'
                },
                {
                  fieldName: 'ToDate',
                  label: 'To Date'
                },
                {
                  fieldName: 'RiskTotal',
                  label: 'Total Risk',
                  format: {
                    digitSeparator: true,
                    places: 2
                  }
                },
                {
                  fieldName: 'RiskDensity',
                  label: 'Risk Density',
                  format: {
                    places: 2,
                    digitSeparator: true
                  }
                }
              ]
            }
          ]
        }
      })

      map.add(wisLayer)
    }

    const legend = new Legend({
      view: view,
      /**
       * Note: Explicitly stating layers to show in legend to limit layers shown
       * i.e. remove 2nd baseline / WIS compare layer containg same risk breakpoints
       */
      layerInfos: [
        {
          layer: baselineLayer,
          title: 'Risk Level'
        },
        {
          layer: structureLayer
        },
        {
          layer: originalBaselineLayer
        }
      ]
    })

    const legendExpand = new Expand({
      view: view,
      content: legend
    })

    // TODO: basemap selection persist through re-renders - low priority change
    const basemapGallery = new BasemapGallery({
      view: view,
      source: [
        Basemap.fromId('satellite'),
        Basemap.fromId('hybrid'),
        Basemap.fromId('dark-gray-vector'),
        Basemap.fromId('gray-vector'),
        Basemap.fromId('terrain'),
        Basemap.fromId('streets-vector')
      ]
    })

    // Create an Expand instance and set the content
    // property to the DOM node of the basemap gallery widget
    // Use an Esri icon font to represent the content inside
    // of the Expand widget
    const bgExpand = new Expand({
      view: view,
      content: basemapGallery
    })

    // Layer visibility toggle
    const layerList = new LayerList({
      view: view
    })

    const layerToggleExpand = new Expand({
      view: view,
      content: layerList
    })

    // Add widgets to view
    view.ui.add(legendExpand, 'top-right')
    view.ui.add(layerToggleExpand, 'top-right')
    view.ui.add(bgExpand, 'bottom-left')

    const expands = [legendExpand, layerToggleExpand, bgExpand]

    legendExpand.watch('expanded', (val) => {
      if (val) {
        handleExpand(legendExpand, expands)
      }
    })

    layerToggleExpand.watch('expanded', (val) => {
      if (val) {
        handleExpand(layerToggleExpand, expands)
      }
    })

    bgExpand.watch('expanded', (val) => {
      if (val) {
        handleExpand(bgExpand, expands)
      }
    })

    const documentClickHandler = (event: MouseEvent) => {
      handleDocumentClick(event, expands)
    }

    document.addEventListener('mousedown', documentClickHandler)

    return () => {
      document.removeEventListener('mousedown', documentClickHandler)

      if (view != null) {
        view.destroy()
      }
    }
  }, [
    assessmentId,
    baseSettings,
    contentFields,
    featureLayerFilterQuery,
    featureLayerTitle,
    featureLayerTitle2,
    featureLayerUrl,
    featureLayerUrl2,
    isEsriTokenSetupComplete,
    layer2Name,
    mapCenterCoordinates,
    onStructureLayerVisibilityChange,
    renderer,
    wisServiceUrl
  ])

  useEffect(() => {
    if (structureLayerRef.current) {
      structureLayerRef.current.visible = showStructureLayer
    }
  }, [showStructureLayer])

  const formatTableNode = useCallback((node: ChildNode) => {
    formatTableNodeFn(node)
  }, [])

  useEffect(() => {
    const target = document.querySelector('.esri-view-root')
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'childList' &&
          'nodeName' in mutation.target &&
          mutation.target.nodeName === 'TABLE'
        ) {
          const childNodes = mutation.addedNodes[0].childNodes
          childNodes.forEach(formatTableNode)
        }
      })
    })

    const config = { childList: true, subtree: true }

    if (target) {
      observer.observe(target, config)
    }

    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [formatTableNode, renderer])

  // check if the Centerline CIMSymbolStroke is white; update as style changes
  return (
    <div
      className={['h-full']
        .concat(
          centerlineColor.every((rgbValue) => rgbValue === 255)
            ? 'esri-white-centerline-symbol-border'
            : ''
        )
        .join(' ')}
      ref={mapDomRef}
      id='mapDiv'
    />
  )
}

export default MapComponent
